import { PipeLineTypeLabels } from 'config'
import { DateTime } from 'luxon'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  AiSchedule,
  AiScheduleFrequency,
  AiScheduleInput,
  AiScheduleLoanFilters,
  AiScheduleThreadMode,
  createAiSchedule,
  getLoanTagSuggestions,
  previewAiScheduleLoans,
  updateAiSchedule,
} from 'services'
import { EmailListInput, Input2, Modal, MultiSelect, Select2, TextArea } from 'stories/components'
import timeZoneMap, { getActiveTimeZone, isValidTimeZone } from 'utils/timezone'

const FREQUENCIES: Record<AiScheduleFrequency, string> = {
  once: 'Once',
  hourly: 'Every hour',
  daily: 'Every day',
  weekly: 'Every week',
  monthly: 'Every month',
}

/** Sunday-first, to match the week as the weekday pickers elsewhere in the app show it. */
const WEEKDAYS = [
  { day: 0, short: 'S', name: 'Sunday' },
  { day: 1, short: 'M', name: 'Monday' },
  { day: 2, short: 'T', name: 'Tuesday' },
  { day: 3, short: 'W', name: 'Wednesday' },
  { day: 4, short: 'T', name: 'Thursday' },
  { day: 5, short: 'F', name: 'Friday' },
  { day: 6, short: 'S', name: 'Saturday' },
]

const THREAD_MODES: Record<AiScheduleThreadMode, string> = {
  continuous: 'Keep one ongoing chat',
  perRun: 'Start a new chat each run',
}

/** What the schedule runs against. Stored as a loan number, a filter, or neither. */
type ScheduleTarget = 'none' | 'loan' | 'set'

const TARGETS: Record<ScheduleTarget, string> = {
  none: 'The project as a whole',
  loan: 'One loan',
  set: 'Every loan matching a filter',
}

const TAG_MATCHES: Record<string, string> = {
  any: 'any of these tags',
  all: 'all of these tags',
}

interface ScheduleDialogProps {
  projectId: number
  /** The schedule being edited, or null to create one. */
  schedule: AiSchedule | null
  /**
   * The loan the panel is open inside. A new schedule is fixed to it - the point of scheduling from
   * inside a loan is that it runs on that loan - and the field is shown read-only rather than hidden,
   * so nobody has to guess what the runs will be about.
   */
  lockedLoanNumber?: number
  onClose: () => void
  onSaved: (schedule: AiSchedule) => void
}

/** Split a stored instant into the date and time parts an <input> wants, read in `zone`. */
const splitRunAt = (runAt: string | undefined, zone: string) => {
  if (!runAt) return { date: '', time: '09:00' }
  const at = DateTime.fromISO(runAt, { zone })
  if (!at.isValid) return { date: '', time: '09:00' }
  return { date: at.toFormat('yyyy-MM-dd'), time: at.toFormat('HH:mm') }
}

/**
 * Create or edit a scheduled run of this project.
 *
 * The form only shows the fields the chosen cadence actually uses: a daily schedule has no weekday
 * to pick and a one-off has no time of day, and showing all of them at once is how a schedule ends
 * up firing at a time nobody chose.
 */
export function ScheduleDialog({ projectId, schedule, lockedLoanNumber, onClose, onSaved }: ScheduleDialogProps) {
  const initialZone = isValidTimeZone(schedule?.timezone) ? (schedule as AiSchedule).timezone : getActiveTimeZone()
  const initialOnce = splitRunAt(schedule?.runAt, initialZone)

  const [name, setName] = useState(schedule?.name || '')
  const [prompt, setPrompt] = useState(schedule?.prompt || '')
  const [frequency, setFrequency] = useState<AiScheduleFrequency>(schedule?.frequency || 'daily')
  const [timeOfDay, setTimeOfDay] = useState(schedule?.timeOfDay || '09:00')
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>(schedule?.daysOfWeek || [])
  const [dayOfMonth, setDayOfMonth] = useState(String(schedule?.dayOfMonth || 1))
  const [minuteOfHour, setMinuteOfHour] = useState(String(schedule?.minuteOfHour ?? 0))
  const [timezone, setTimezone] = useState(initialZone)
  const [onceDate, setOnceDate] = useState(initialOnce.date)
  const [onceTime, setOnceTime] = useState(initialOnce.time)
  const [loanNumber, setLoanNumber] = useState(String(schedule?.loanNumber || lockedLoanNumber || ''))
  const [target, setTarget] = useState<ScheduleTarget>(
    schedule?.loanFilters ? 'set' : schedule?.loanNumber || lockedLoanNumber ? 'loan' : 'none',
  )
  const [tags, setTags] = useState<string[]>(schedule?.loanFilters?.tags || [])
  const [excludeTags, setExcludeTags] = useState<string[]>(schedule?.loanFilters?.excludeTags || [])
  const [tagMatch, setTagMatch] = useState<string>(schedule?.loanFilters?.tagMatch || 'any')
  const [pipelineType, setPipelineType] = useState<string>(schedule?.loanFilters?.pipelineType || 'Servicing')
  const [maxLoansPerRun, setMaxLoansPerRun] = useState(String(schedule?.maxLoansPerRun ?? 25))
  const [tagOptions, setTagOptions] = useState<string[]>([])
  const [matchCount, setMatchCount] = useState<number | null>(null)
  const [checking, setChecking] = useState(false)
  const [threadMode, setThreadMode] = useState<AiScheduleThreadMode>(schedule?.threadMode || 'continuous')
  const [historyTurns, setHistoryTurns] = useState(String(schedule?.historyTurns ?? 6))
  const [notifyEmails, setNotifyEmails] = useState<string[]>(schedule?.notifyEmails || [])
  const [saving, setSaving] = useState(false)

  const loanLocked = !schedule && !!lockedLoanNumber
  const zoneOptions = useMemo(() => timeZoneMap, [])

  useEffect(() => {
    if (target !== 'set') return
    getLoanTagSuggestions()
      .then((suggestions) => setTagOptions(suggestions || []))
      .catch(() => setTagOptions([]))
  }, [target])

  /** The filter as the API stores it. Built in one place so the dialog and the check agree. */
  const currentFilters = (): AiScheduleLoanFilters => ({
    tags,
    tagMatch: tagMatch === 'all' ? 'all' : 'any',
    excludeTags,
    pipelineType,
  })

  const onCheckMatches = async () => {
    if (!tags.length && !excludeTags.length) {
      toast('Pick at least one tag first.', { type: 'error' })
      return
    }
    setChecking(true)
    try {
      const result = await previewAiScheduleLoans(projectId, currentFilters())
      setMatchCount(result?.count ?? 0)
    } catch {
      setMatchCount(null)
    } finally {
      setChecking(false)
    }
  }

  const toggleDay = (day: number) =>
    setDaysOfWeek((prev) => (prev.includes(day) ? prev.filter((item) => item !== day) : [...prev, day].sort()))

  const onSave = async () => {
    if (!name.trim()) {
      toast('Give the schedule a name.', { type: 'error' })
      return
    }
    if (!prompt.trim()) {
      toast('Write the prompt this schedule should run.', { type: 'error' })
      return
    }

    const data: AiScheduleInput = {
      name: name.trim(),
      prompt,
      frequency,
      timeOfDay,
      daysOfWeek,
      dayOfMonth: Number(dayOfMonth) || 1,
      minuteOfHour: Number(minuteOfHour) || 0,
      timezone,
      // One or the other: the API rejects a schedule carrying both.
      loanNumber: target === 'loan' ? Number(loanNumber) || undefined : undefined,
      loanFilters: target === 'set' ? currentFilters() : null,
      maxLoansPerRun: Number(maxLoansPerRun) || 25,
      threadMode,
      historyTurns: Number(historyTurns) || 0,
      notifyEmails,
    }

    if (target === 'set' && !tags.length && !excludeTags.length) {
      toast('Pick the tags that choose the loans this runs on.', { type: 'error' })
      return
    }
    if (target === 'loan' && !Number(loanNumber)) {
      toast('Give the loan number this runs on.', { type: 'error' })
      return
    }

    if (frequency === 'once') {
      if (!onceDate) {
        toast('Pick the date and time to run.', { type: 'error' })
        return
      }
      // Built in the schedule's own zone and sent with its offset. A naive "2026-10-20T09:00"
      // would be read in the server's zone, which is UTC in every deployed environment.
      const at = DateTime.fromISO(`${onceDate}T${onceTime || '09:00'}`, { zone: timezone })
      if (!at.isValid) {
        toast('That date and time could not be read.', { type: 'error' })
        return
      }
      if (at <= DateTime.now()) {
        toast('A one-off schedule has to be in the future.', { type: 'error' })
        return
      }
      data.runAt = at.toISO() as string
    }

    setSaving(true)
    try {
      const saved = schedule ? await updateAiSchedule(schedule.id, data) : await createAiSchedule(projectId, data)
      onSaved(saved)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title={schedule ? 'Edit schedule' : 'New schedule'}
      titleOkay={schedule ? 'Save' : 'Create'}
      isOpen
      loading={saving}
      onOk={onSave}
      onClose={onClose}
    >
      <div className="w-[34rem] max-w-full">
        <Input2 title="Name" required value={name} onChange={(value: string) => setName(value)} />

        <div className="mt-4">
          <TextArea
            title="Prompt"
            required
            rows={6}
            value={prompt}
            onChange={(value: string) => setPrompt(value)}
          />
          <p className="text-xs text-gray-500 mt-1">
            Exactly what you would have typed in the chat. It runs with this project&apos;s instructions,
            knowledge and connectors, as you.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <Select2
            id="frequency"
            title="How often"
            options={FREQUENCIES}
            hasDefaultOption={false}
            value={frequency}
            onChange={(value: string) => setFrequency(value as AiScheduleFrequency)}
          />
          {frequency === 'once' ? (
            <Input2 title="Date" type="date" required value={onceDate} onChange={(value: string) => setOnceDate(value)} />
          ) : frequency === 'hourly' ? (
            <Input2
              title="Minutes past the hour"
              type="number"
              value={minuteOfHour}
              onChange={(value: string) => setMinuteOfHour(value)}
            />
          ) : (
            <Input2 title="Time" type="time" value={timeOfDay} onChange={(value: string) => setTimeOfDay(value)} />
          )}
        </div>

        {frequency === 'once' && (
          <div className="mt-3 w-1/2 pr-1.5">
            <Input2 title="Time" type="time" value={onceTime} onChange={(value: string) => setOnceTime(value)} />
          </div>
        )}

        {frequency === 'weekly' && (
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-700 mb-1.5">On these days</p>
            <div className="flex items-center gap-1.5">
              {WEEKDAYS.map((weekday) => {
                const picked = daysOfWeek.includes(weekday.day)
                return (
                  <button
                    key={weekday.day}
                    type="button"
                    title={weekday.name}
                    className={`w-9 h-9 rounded-full text-sm font-semibold border ${
                      picked
                        ? 'bg-shade-blue text-white border-shade-blue'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-shade-blue'
                    }`}
                    onClick={() => toggleDay(weekday.day)}
                  >
                    {weekday.short}
                  </button>
                )
              })}
            </div>
            {!daysOfWeek.length && <p className="text-xs text-gray-500 mt-1">No day picked - it will run every day.</p>}
          </div>
        )}

        {frequency === 'monthly' && (
          <div className="mt-3 w-1/2 pr-1.5">
            <Input2
              title="Day of the month"
              type="number"
              value={dayOfMonth}
              onChange={(value: string) => setDayOfMonth(value)}
            />
            <p className="text-xs text-gray-500 mt-1">A day past the end of a short month runs on its last day.</p>
          </div>
        )}

        <div className="mt-4">
          <Select2
            id="timezone"
            title="Timezone"
            options={zoneOptions}
            hasDefaultOption={false}
            value={timezone}
            onChange={(value: string) => setTimezone(value)}
          />
        </div>

        <div className="mt-4">
          {loanLocked ? (
            <>
              <p className="text-sm font-medium text-gray-700">Runs on</p>
              <p className="text-sm text-gray-600 mt-0.5">
                Loan {lockedLoanNumber} - each run is filed in this loan&apos;s chat.
              </p>
            </>
          ) : (
            <>
              <Select2
                id="target"
                title="Runs on"
                options={TARGETS}
                hasDefaultOption={false}
                value={target}
                onChange={(value: string) => {
                  setTarget(value as ScheduleTarget)
                  setMatchCount(null)
                }}
              />

              {target === 'none' && (
                <p className="text-xs text-gray-500 mt-1">
                  One run about the book as a whole, kept as a chat in this project.
                </p>
              )}

              {target === 'loan' && (
                <div className="mt-3">
                  <Input2
                    title="Loan number"
                    type="number"
                    required
                    value={loanNumber}
                    onChange={(value: string) => setLoanNumber(value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Every run is about that loan, and its chat is filed on the loan as well as here.
                  </p>
                </div>
              )}

              {target === 'set' && (
                <div className="mt-3 border border-gray-200 rounded-md p-3">
                  <div className="grid grid-cols-2 gap-3">
                    <MultiSelect
                      id="schedule-tags"
                      title="Loans tagged"
                      options={tagOptions}
                      value={tags}
                      defaultSelected={false}
                      allowCustomValues
                      onChange={(value: string[]) => {
                        setTags(value || [])
                        setMatchCount(null)
                      }}
                    />
                    <Select2
                      id="tagMatch"
                      title="Matching"
                      options={TAG_MATCHES}
                      hasDefaultOption={false}
                      value={tagMatch}
                      onChange={(value: string) => {
                        setTagMatch(value)
                        setMatchCount(null)
                      }}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <MultiSelect
                      id="schedule-exclude-tags"
                      title="But not tagged"
                      options={tagOptions}
                      value={excludeTags}
                      defaultSelected={false}
                      allowCustomValues
                      onChange={(value: string[]) => {
                        setExcludeTags(value || [])
                        setMatchCount(null)
                      }}
                    />
                    <Select2
                      id="pipelineType"
                      title="From"
                      options={PipeLineTypeLabels}
                      hasDefaultOption={false}
                      value={pipelineType}
                      onChange={(value: string) => {
                        setPipelineType(value)
                        setMatchCount(null)
                      }}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3 items-end">
                    <Input2
                      title="Most loans per run"
                      type="number"
                      value={maxLoansPerRun}
                      onChange={(value: string) => setMaxLoansPerRun(value)}
                    />
                    <button
                      type="button"
                      className="text-sm text-shade-blue hover:underline text-left pb-2 disabled:opacity-50"
                      disabled={checking}
                      onClick={onCheckMatches}
                    >
                      {checking ? 'Checking...' : 'How many loans match?'}
                    </button>
                  </div>
                  {matchCount !== null && (
                    <p className="text-xs text-gray-600 mt-2">
                      {matchCount === 0
                        ? 'No loans match right now - the schedule would record a skipped run.'
                        : `${matchCount} loan${matchCount === 1 ? '' : 's'} match right now${
                            matchCount > (Number(maxLoansPerRun) || 25)
                              ? `; the first ${Number(maxLoansPerRun) || 25} would run.`
                              : '.'
                          }`}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    The filter is re-read every time it runs, so a loan tagged tomorrow is included then.
                    Each loan is a full assistant turn and keeps its own chat on that loan.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="mt-4">
          <Select2
            id="threadMode"
            title="Chat history"
            options={THREAD_MODES}
            hasDefaultOption={false}
            value={threadMode}
            onChange={(value: string) => setThreadMode(value as AiScheduleThreadMode)}
          />
          <p className="text-xs text-gray-500 mt-1">
            {threadMode === 'continuous'
              ? 'Every run continues the same chat and can see what it said before, so it can answer in terms of what has changed.'
              : 'Every run starts its own chat, for an answer that should stand on its own.'}
          </p>
        </div>

        {threadMode === 'continuous' && (
          <div className="mt-3 w-1/2 pr-1.5">
            <Input2
              title="Earlier turns to re-read"
              type="number"
              value={historyTurns}
              onChange={(value: string) => setHistoryTurns(value)}
            />
            <p className="text-xs text-gray-500 mt-1">0 runs the prompt cold; the chat still keeps every run.</p>
          </div>
        )}

        <div className="mt-4">
          <EmailListInput
            title="Email the answer to"
            value={notifyEmails}
            placeholder="Nobody - the answer stays in the chat"
            onChange={(value: string[]) => setNotifyEmails(value)}
          />
        </div>
      </div>
    </Modal>
  )
}
